/**
 * TaskAlertsCard — Paramètres → Notifications : rappels d'échéance par défaut des tâches + résumé périodique (Batch #14.286).
 * Réglages indépendants (GET/PUT /tasks/settings). Canaux par événement : « Rappel d'échéance de tâche » / « Résumé périodique des tâches ».
 */
import { useEffect, useState } from "react";
import { BellRing, Loader2, Mail, Play, Save } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { ALERT_OPTIONS, WEEKDAYS } from "../tasks/taskHelpers";

const FREQS = [["daily", "Chaque jour"], ["weekly", "Chaque semaine"], ["monthly", "Chaque mois"]];
const STATUSES = [["a_faire", "À faire"], ["en_cours", "En cours"], ["en_attente", "En attente"]];
const sel = "border rounded-lg px-2 py-1.5 text-sm bg-white";

export default function TaskAlertsCard() {
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/tasks/settings").then((r) => { setCfg(r.data); setSaved(JSON.stringify(r.data)); }).catch(() => toast.error("Réglages des tâches indisponibles"));
  }, []);

  if (!cfg) return null;
  const s = cfg.summary;
  const dirty = JSON.stringify(cfg) !== saved;
  const setS = (k, v) => setCfg({ ...cfg, summary: { ...s, [k]: v } });
  const toggleAlert = (id) => setCfg({ ...cfg, default_alerts: cfg.default_alerts.includes(id) ? cfg.default_alerts.filter((a) => a !== id) : [...cfg.default_alerts, id] });
  const toggleStatus = (id) => setS("statuses", s.statuses.includes(id) ? s.statuses.filter((x) => x !== id) : [...s.statuses, id]);

  const save = async () => {
    setBusy("save");
    try { const r = await api.put("/tasks/settings", { default_alerts: cfg.default_alerts, summary: s }); setCfg(r.data); setSaved(JSON.stringify(r.data)); toast.success("Réglages des tâches enregistrés"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };
  const runAlerts = async () => {
    setBusy("alerts");
    try { const r = await api.post("/tasks/alerts/run-now"); toast.success(`${r.data.checked} tâche(s) vérifiée(s) · ${r.data.sent} rappel(s) envoyé(s)${r.data.emailed ? " · courriel envoyé" : ""}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };
  const sendSummary = async () => {
    setBusy("summary");
    try { const r = await api.post("/tasks/summary/send-now"); toast.success(r.data.emailed ? `Résumé envoyé (${r.data.total} tâche(s))` : `Résumé généré (${r.data.total} tâche(s)) — courriel non envoyé (adresse cabinet ou SMTP manquant)`); const g = await api.get("/tasks/settings"); setCfg(g.data); setSaved(JSON.stringify(g.data)); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="task-alerts-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-sm flex items-center gap-2"><BellRing className="w-4 h-4 text-emerald-600" /> Tâches — rappels d'échéance et résumé périodique</h3>
        {dirty && (
          <button onClick={save} disabled={busy === "save"} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="task-alerts-save">
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
          </button>
        )}
      </div>

      <div>
        <div className="text-xs font-semibold text-slate-700 mb-1">Rappels par défaut des nouvelles tâches</div>
        <p className="text-[11px] text-slate-500 mb-2">Chaque tâche peut ensuite ajuster ses rappels. Passe automatique toutes les 15 minutes. Canaux (courriel · push · cloche) : événement « Rappel d'échéance de tâche » ci-dessous.</p>
        <div className="flex flex-wrap gap-1.5" data-testid="task-default-alerts">
          {ALERT_OPTIONS.map((a) => {
            const on = cfg.default_alerts.includes(a.id);
            return (
              <button key={a.id} type="button" onClick={() => toggleAlert(a.id)}
                className={`px-2.5 py-1 rounded-full text-xs border transition ${on ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-emerald-400"}`}
                data-testid={`task-default-alert-${a.id}`}>{a.label}</button>
            );
          })}
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <div className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Résumé des tâches non faites / en cours</div>
          <button onClick={() => setS("enabled", !s.enabled)} className={`px-3 py-1 rounded-lg text-xs font-semibold border ${s.enabled ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-gray-50 text-gray-500"}`} data-testid="task-summary-toggle">
            {s.enabled ? "Activé" : "Désactivé"}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mb-2">Courriel récapitulatif (en retard · aujourd'hui · 7 jours · plus tard · sans échéance), même pour les tâches non périodiques. Canal : événement « Résumé périodique des tâches ».</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select value={s.freq} onChange={(e) => setS("freq", e.target.value)} className={sel} data-testid="task-summary-freq">{FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          {s.freq === "weekly" && <select value={s.weekday} onChange={(e) => setS("weekday", Number(e.target.value))} className={sel} data-testid="task-summary-weekday">{WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select>}
          {s.freq === "monthly" && <span className="inline-flex items-center gap-1">le <input type="number" min={1} max={28} value={s.day_of_month} onChange={(e) => setS("day_of_month", Number(e.target.value))} className={`${sel} w-16`} data-testid="task-summary-day" /></span>}
          <span className="text-slate-500">à</span>
          <select value={s.hour} onChange={(e) => setS("hour", Number(e.target.value))} className={sel} data-testid="task-summary-hour">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")} h 00</option>)}</select>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="task-summary-statuses">
          <span className="text-[11px] text-slate-500 mr-1">Inclure :</span>
          {STATUSES.map(([id, l]) => (
            <button key={id} type="button" onClick={() => toggleStatus(id)} className={`px-2 py-0.5 rounded-full text-[11px] border ${s.statuses.includes(id) ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-500"}`} data-testid={`task-summary-status-${id}`}>{l}</button>
          ))}
          <button type="button" onClick={() => setS("include_no_due", !s.include_no_due)} className={`px-2 py-0.5 rounded-full text-[11px] border ${s.include_no_due ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-500"}`} data-testid="task-summary-no-due">Sans échéance</button>
        </div>
        {cfg.summary_last_sent?.at && <p className="text-[11px] text-slate-400 mt-2" data-testid="task-summary-last">Dernier envoi planifié : {new Date(cfg.summary_last_sent.at).toLocaleString("fr-CA")} ({cfg.summary_last_sent.total} tâche(s))</p>}
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={runAlerts} disabled={!!busy} className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="task-alerts-run-now">
          {busy === "alerts" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Lancer la passe de rappels
        </button>
        <button onClick={sendSummary} disabled={!!busy} className="border border-sky-300 text-sky-700 hover:bg-sky-50 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="task-summary-send-now">
          {busy === "summary" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Envoyer le résumé maintenant
        </button>
      </div>
    </div>
  );
}
